import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  DASHBOARD_WEEKDAY_LABELS,
  buildMonthCells,
  formatMonthTitle,
  isDateInRange,
  toIsoDate,
} from '@/components/Gestores/gestorDashboardDateRangeUtils'

type Props = {
  viewYear: number
  viewMonth: number
  draftFrom: string
  draftTo: string
  onMonthChange: (year: number, month: number) => void
  onDaySelect: (iso: string) => void
}

export function GestorDashboardDateRangeCalendar({
  viewYear,
  viewMonth,
  draftFrom,
  draftTo,
  onMonthChange,
  onDaySelect,
}: Props) {
  const rangeEnd = draftTo || draftFrom
  const today = toIsoDate(new Date())
  const cells = buildMonthCells(viewYear, viewMonth)

  const shiftMonth = (delta: number) => {
    const next = new Date(viewYear, viewMonth + delta, 1)
    onMonthChange(next.getFullYear(), next.getMonth())
  }

  return (
    <div>
      <div className='mb-3 flex items-center justify-between'>
        <button
          type='button'
          onClick={() => shiftMonth(-1)}
          className='flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-white/[0.06] hover:text-text-primary'
          aria-label='Mês anterior'
        >
          <ChevronLeft size={16} />
        </button>
        <span className='text-sm font-semibold text-text-primary'>{formatMonthTitle(viewYear, viewMonth)}</span>
        <button
          type='button'
          onClick={() => shiftMonth(1)}
          className='flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-white/[0.06] hover:text-text-primary'
          aria-label='Próximo mês'
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className='grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-text-muted'>
        {DASHBOARD_WEEKDAY_LABELS.map((weekday) => (
          <span key={weekday.key} className='py-1'>
            {weekday.label}
          </span>
        ))}
      </div>

      <div className='mt-1 grid grid-cols-7 gap-1'>
        {cells.map(({ iso, inMonth }) => {
          const selectedStart = draftFrom === iso
          const selectedEnd = rangeEnd === iso
          const inRange = draftFrom && rangeEnd ? isDateInRange(iso, draftFrom, rangeEnd) : false
          const isToday = iso === today

          return (
            <button
              key={iso}
              type='button'
              onClick={() => onDaySelect(iso)}
              className={cn(
                'relative flex h-9 items-center justify-center rounded-full text-sm transition-colors',
                !inMonth && 'text-text-muted/40',
                inMonth && !inRange && 'text-text-primary hover:bg-white/[0.06]',
                inRange && !selectedStart && !selectedEnd && 'bg-accent/15 text-accent',
                (selectedStart || selectedEnd) && 'bg-accent font-semibold text-white',
                isToday && !inRange && 'ring-1 ring-accent/40',
              )}
            >
              {parseIsoDay(iso)}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function parseIsoDay(iso: string): number {
  return Number(iso.slice(8, 10))
}
